import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, Upload, FileCheck, X, ChevronLeft, Loader2, AlertTriangle, CheckCircle2, Info, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PrivacyBadge from '@/components/PrivacyBadge';
import apiService from '@/services/apiService';
import { useApp } from '@/context/AppContext';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';

// Constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const VALID_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

// Document types that allow multiple uploads
const MULTI_UPLOAD_TYPES = [
  'Proof of sufficient financial means (bank statements / income proof)',
  'Bank Statement',
  'bank statement',
  'financial'
];

const UploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedDocument, visaType } = location.state || {};
  const { trackEvent } = useAnalytics();
  
  const { 
    addToQueue, 
    replaceInQueue, 
    isInQueue, 
    getFromQueue,
    uploadQueue 
  } = useApp();
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  
  // Type verification state
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [showMismatchWarning, setShowMismatchWarning] = useState(false);
  
  // Duplicate handling state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);

  // Check if this document type allows multiple uploads
  const isMultiUploadType = selectedDocument?.name && 
    MULTI_UPLOAD_TYPES.some(type => 
      selectedDocument.name.toLowerCase().includes(type.toLowerCase()) ||
      type.toLowerCase().includes(selectedDocument.name.toLowerCase())
    );
  
  // Get existing uploads for this document type
  const existingUploads = selectedDocument?.name ? 
    uploadQueue.filter(item => item.documentType === selectedDocument.name) : [];
  const existingInQueue = existingUploads.length > 0 ? existingUploads[0] : null;

  useEffect(() => {
    if (!visaType) {
      navigate('/select-country');
    }
  }, [visaType, navigate]);

  // Validate file before accepting
  const validateFile = useCallback((file) => {
    if (!VALID_FILE_TYPES.includes(file.type)) {
      return 'Please upload a PDF, JPG, or PNG file';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be under 10MB';
    }
    return null;
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer?.files || []);
    const validFiles = [];
    
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        alert(`${file.name}: ${error}`);
        continue;
      }
      validFiles.push(file);
    }
    
    if (isMultiUploadType) {
      // For multi-upload types, add to existing files (max 6)
      setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 6));
    } else {
      // For single upload, replace
      setSelectedFiles(validFiles.slice(0, 1));
    }
    
    setUploadSuccess(false);
    setVerificationResult(null);
  }, [validateFile, isMultiUploadType]);

  const handleFileInput = useCallback((e) => {
    const files = Array.from(e.target?.files || []);
    const validFiles = [];
    
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        alert(`${file.name}: ${error}`);
        continue;
      }
      validFiles.push(file);
    }
    
    if (isMultiUploadType) {
      setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 6));
    } else {
      setSelectedFiles(validFiles.slice(0, 1));
    }
    
    setUploadSuccess(false);
    setVerificationResult(null);
  }, [validateFile, isMultiUploadType]);

  const removeFile = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setVerificationResult(null);
  }, []);

  const handleUploadClick = async () => {
    if (selectedFiles.length === 0 || isUploading) return;
    
    setIsUploading(true);
    setUploadedCount(0);
    
    const documentType = selectedDocument?.name || 'Other Document';
    const uploadedItems = [];
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadStep(`Uploading document ${i + 1} of ${selectedFiles.length}...`);
        
        // Upload file to backend
        const uploadResult = await apiService.uploadPendingDocument(
          file, 
          documentType
        );
        
        const newPendingId = uploadResult.pending_id;
        
        // Verify document type for first file only (to save API calls)
        if (i === 0) {
          setUploadStep('Verifying document type...');
          setIsVerifying(true);
          try {
            const verification = await apiService.verifyDocumentType(newPendingId);
            setVerificationResult(verification);
            
            if (verification.is_mismatch) {
              setShowMismatchWarning(true);
              // Store pending upload info for after user decision
              setPendingUpload({
                pendingId: newPendingId,
                documentType,
                filename: file.name,
                fileSize: file.size,
                selectedDocument,
                verification
              });
              setIsVerifying(false);
              setIsUploading(false);
              setUploadStep('');
              return; // Wait for user decision
            }
          } catch (verifyError) {
            console.log('Verification skipped:', verifyError);
            // Continue without verification if it fails
          }
          setIsVerifying(false);
        }
        
        uploadedItems.push({
          pendingId: newPendingId,
          documentType: isMultiUploadType && selectedFiles.length > 1 
            ? `${documentType} (${i + 1} of ${selectedFiles.length})`
            : documentType,
          filename: file.name,
          fileSize: file.size,
          selectedDocument,
          monthIndex: isMultiUploadType ? i + 1 : null
        });
        
        setUploadedCount(i + 1);
      }
      
      // Check for duplicates (only for non-multi-upload types)
      if (!isMultiUploadType && isInQueue(documentType)) {
        setDuplicateInfo({
          existing: getFromQueue(documentType),
          documentType
        });
        setPendingUpload(uploadedItems[0]);
        setShowDuplicateModal(true);
        setIsUploading(false);
        setUploadStep('');
        return;
      }
      
      // Add all items to queue
      for (const item of uploadedItems) {
        addToQueue(item);
      }
      
      // Track upload completion
      trackEvent(ANALYTICS_EVENTS.DOCUMENT_UPLOAD_COMPLETED, {
        document_type: documentType,
        file_count: uploadedItems.length,
        visa_type: visaType
      });
      
      setUploadSuccess(true);
      setIsUploading(false);
      setUploadStep('');
      
      // Navigate back to document list after short delay
      setTimeout(() => {
        navigate('/documents', { state: { visaType } });
      }, 1500);
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Upload failed';
      alert(`Upload failed: ${errorMessage}`);
      setIsUploading(false);
      setUploadStep('');
    }
  };

  const handleProceedWithMismatch = async () => {
    if (!pendingUpload) return;
    
    setShowMismatchWarning(false);
    setIsUploading(true);
    setUploadStep('Continuing upload...');
    
    const documentType = selectedDocument?.name || 'Other Document';
    const uploadedItems = [pendingUpload];
    
    try {
      // Upload remaining files if any
      for (let i = 1; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadStep(`Uploading document ${i + 1} of ${selectedFiles.length}...`);
        
        const uploadResult = await apiService.uploadPendingDocument(file, documentType);
        
        uploadedItems.push({
          pendingId: uploadResult.pending_id,
          documentType: isMultiUploadType && selectedFiles.length > 1 
            ? `${documentType} (${i + 1} of ${selectedFiles.length})`
            : documentType,
          filename: file.name,
          fileSize: file.size,
          selectedDocument,
          monthIndex: isMultiUploadType ? i + 1 : null
        });
        
        setUploadedCount(i + 1);
      }
      
      // Add all items to queue
      for (const item of uploadedItems) {
        addToQueue(item);
      }
      
      setUploadSuccess(true);
      setIsUploading(false);
      setUploadStep('');
      setPendingUpload(null);
      
      setTimeout(() => {
        navigate('/documents', { state: { visaType } });
      }, 1500);
      
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
      setIsUploading(false);
      setUploadStep('');
    }
  };

  const handleCancelMismatch = () => {
    setShowMismatchWarning(false);
    setPendingUpload(null);
    setSelectedFiles([]);
    setVerificationResult(null);
  };

  const handleReplaceDuplicate = () => {
    if (pendingUpload) {
      replaceInQueue(pendingUpload.documentType, pendingUpload);
      setShowDuplicateModal(false);
      setUploadSuccess(true);
      
      setTimeout(() => {
        navigate('/documents', { state: { visaType } });
      }, 1500);
    }
  };

  const handleKeepExisting = () => {
    setShowDuplicateModal(false);
    setPendingUpload(null);
    setDuplicateInfo(null);
    setSelectedFiles([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/documents', { state: { visaType } })}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back to Checklist</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <PrivacyBadge variant="small" />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Document Type Header */}
          {selectedDocument ? (
            <div className="mb-8">
              <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-2 text-center">
                Upload: {selectedDocument.name}
              </h1>
              <p className="font-public text-lg text-slate-600 mb-4 text-center">
                {visaType}
              </p>
              
              {/* Requirements for this document */}
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {selectedDocument.requires_apostille && (
                  <span className="px-3 py-1 bg-warning/10 text-warning border border-warning/20 rounded-full text-sm">
                    Apostille Required
                  </span>
                )}
                {selectedDocument.requires_translation && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 border border-blue-200 rounded-full text-sm">
                    Translation Required
                  </span>
                )}
                {selectedDocument.category && (
                  <span className="px-3 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-full text-sm">
                    {selectedDocument.category}
                  </span>
                )}
              </div>
              
              {/* Multi-upload info for bank statements */}
              {isMultiUploadType && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mb-6">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-public font-semibold text-blue-900">Multiple Documents Recommended</p>
                    <p className="text-sm text-blue-700">
                      Upload 3-6 months of bank statements for a complete financial proof. 
                      You can select multiple files at once or add them one by one.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Already uploaded info (for non-multi-upload types) */}
              {!isMultiUploadType && existingInQueue && (
                <div className="bg-verify/10 border border-verify/30 rounded-lg p-4 flex items-start gap-3 mb-6">
                  <FileCheck className="w-5 h-5 text-verify flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-public font-semibold text-slate-900">Document already in queue</p>
                    <p className="text-sm text-slate-600">
                      "{existingInQueue.filename}" is ready for analysis. 
                      Want to use a different file? Upload it here and it will replace the current one.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Show existing bank statements */}
              {isMultiUploadType && existingUploads.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                  <p className="font-public font-semibold text-slate-900 mb-2">
                    Already in queue: {existingUploads.length} statement{existingUploads.length > 1 ? 's' : ''}
                  </p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {existingUploads.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-verify" />
                        {item.filename}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-8">
              <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-2 text-center">
                Upload Document
              </h1>
              <p className="font-public text-lg text-slate-600 text-center">
                Upload any document for AI analysis
              </p>
            </div>
          )}

          <div className="mb-8">
            <PrivacyBadge />
          </div>

          {/* Success State */}
          <AnimatePresence>
            {uploadSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-verify/10 border-2 border-verify rounded-lg p-8 text-center mb-8"
              >
                <CheckCircle2 className="w-16 h-16 text-verify mx-auto mb-4" />
                <h3 className="font-outfit font-bold text-xl text-slate-900 mb-2">
                  {uploadedCount > 1 ? `${uploadedCount} Documents Added to Queue!` : 'Document Added to Queue!'}
                </h3>
                <p className="font-public text-slate-600">
                  Returning to document checklist...
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Upload Area */}
          {!uploadSuccess && (
            <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
              {selectedFiles.length === 0 ? (
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-12 text-center transition-all ${
                    dragActive ? 'border-trust bg-trust/5' : 'border-slate-300 hover:border-trust/50'
                  }`}
                  data-testid="upload-dropzone"
                >
                  <div className="bg-trust/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-trust" />
                  </div>
                  <h3 className="font-public font-semibold text-lg text-slate-900 mb-2">
                    {isMultiUploadType ? 'Drag and drop your files here' : 'Drag and drop your file here'}
                  </h3>
                  <p className="font-public text-slate-600 mb-4">or</p>
                  <label className="inline-block">
                    <input
                      type="file"
                      onChange={handleFileInput}
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple={isMultiUploadType}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <span className="bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-medium hover:bg-trust/90 active:scale-95 transition-all cursor-pointer inline-block shadow-sm">
                      {isMultiUploadType ? 'Choose Files' : 'Choose File'}
                    </span>
                  </label>
                  <p className="font-public text-sm text-slate-500 mt-4">
                    Supported formats: PDF, JPG, PNG (Max 10MB each)
                    {isMultiUploadType && <span className="block mt-1">You can select multiple files</span>}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedFiles.map((file, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="border-2 border-verify rounded-lg p-4 bg-verify/5"
                      data-testid={`file-preview-${index}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="bg-verify/10 p-3 rounded-md">
                            <FileCheck className="w-6 h-6 text-verify" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-public font-semibold text-slate-900 mb-1 truncate">
                              {file.name}
                            </h4>
                            <p className="font-public text-sm text-slate-600">
                              {file.type.split('/')[1]?.toUpperCase() || 'FILE'} • {formatFileSize(file.size)}
                              {isMultiUploadType && <span className="ml-2 text-trust">Month {index + 1}</span>}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="p-2 hover:bg-slate-200 rounded-md transition-colors flex-shrink-0"
                          data-testid={`remove-file-${index}`}
                          aria-label="Remove file"
                        >
                          <X className="w-5 h-5 text-slate-600" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Add more files button for multi-upload */}
                  {isMultiUploadType && selectedFiles.length < 6 && (
                    <label className="block">
                      <input
                        type="file"
                        onChange={handleFileInput}
                        accept=".pdf,.jpg,.jpeg,.png"
                        multiple
                        className="hidden"
                      />
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-trust/50 transition-colors">
                        <Plus className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                        <span className="text-sm text-slate-600">
                          Add more statements ({6 - selectedFiles.length} remaining)
                        </span>
                      </div>
                    </label>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Upload Button */}
          {selectedFiles.length > 0 && !uploadSuccess && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 text-center"
            >
              <button
                onClick={handleUploadClick}
                disabled={isUploading || isVerifying}
                className={`bg-trust text-trust-foreground px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust/90 active:scale-95 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2 ${
                  isUploading || isVerifying ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="upload-button"
              >
                {isUploading || isVerifying ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {uploadStep || 'Processing...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Add {selectedFiles.length > 1 ? `${selectedFiles.length} Files` : ''} to Queue
                  </>
                )}
              </button>
              <p className="text-xs text-slate-500 mt-2">
                {isMultiUploadType && selectedFiles.length < 3 
                  ? 'Tip: Upload at least 3 months of statements for best results'
                  : 'You can upload more documents before proceeding to payment'}
              </p>
            </motion.div>
          )}

          {/* Queue status */}
          {uploadQueue.length > 0 && !uploadSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8 bg-slate-50 border border-slate-200 rounded-lg p-4 text-center"
            >
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-trust">{uploadQueue.length}</span> document{uploadQueue.length > 1 ? 's' : ''} in your upload queue
              </p>
            </motion.div>
          )}

          {/* Privacy Notice Banner */}
          <div className="mt-8 bg-verify/5 border border-verify/30 rounded-lg p-4" data-testid="privacy-notice">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-verify flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-public font-semibold text-slate-900 mb-1">Your Privacy is Protected</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Documents are processed temporarily and deleted immediately after analysis</li>
                  <li>• Personal information is automatically redacted before AI review</li>
                  <li>• We never store your original documents</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-public font-semibold text-slate-900 mb-3">How it works</h3>
            <ol className="space-y-2 font-public text-sm text-slate-600">
              {[
                'Upload documents you want checked',
                'AI verifies document type matches your selection',
                'Add more documents or proceed to payment',
                'Complete secure payment',
                'AI identifies common issues against Spain visa requirements',
                'Get readiness reports highlighting potential risks'
              ].map((step, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-trust font-semibold">{idx + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Disclaimer */}
          <div className="mt-4 text-center">
            <p className="text-xs text-slate-500">
              This tool identifies common document issues. It does not provide legal advice, official certification, or guarantee visa approval.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Document Type Mismatch Warning Modal */}
      <AnimatePresence>
        {showMismatchWarning && verificationResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-warning/10 p-3 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-warning" />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-lg text-slate-900 mb-1">
                    Document Type Mismatch
                  </h3>
                  <p className="text-slate-600 text-sm">
                    The uploaded document appears to be different from what you selected.
                  </p>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-2">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Expected:</span> {verificationResult.expected_type}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Detected:</span> {verificationResult.detected_type}
                  {verificationResult.confidence && (
                    <span className="text-slate-400 ml-1">({verificationResult.confidence}% confidence)</span>
                  )}
                </p>
                {verificationResult.mismatch_reason && (
                  <p className="text-sm text-slate-500 italic mt-2">
                    {verificationResult.mismatch_reason}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                You can still proceed, but make sure you're uploading the correct document for your visa application.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={handleCancelMismatch}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-md font-public font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="cancel-mismatch-button"
                >
                  Choose Different File
                </button>
                <button
                  onClick={handleProceedWithMismatch}
                  className="flex-1 px-4 py-2 bg-warning text-white rounded-md font-public font-medium hover:bg-warning/90 transition-colors"
                  data-testid="proceed-mismatch-button"
                >
                  Proceed Anyway
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Duplicate Document Modal */}
      <AnimatePresence>
        {showDuplicateModal && duplicateInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-warning/10 p-3 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-warning" />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-lg text-slate-900 mb-1">
                    Duplicate Document
                  </h3>
                  <p className="text-slate-600 text-sm">
                    You've already uploaded a file for "{duplicateInfo.documentType}". 
                    Would you like to replace it?
                  </p>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-600 mb-2">
                  <span className="font-medium">Current file:</span> {duplicateInfo.existing?.filename}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">New file:</span> {selectedFiles[0]?.name}
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleKeepExisting}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-md font-public font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="keep-existing-button"
                >
                  Keep Existing
                </button>
                <button
                  onClick={handleReplaceDuplicate}
                  className="flex-1 px-4 py-2 bg-warning text-white rounded-md font-public font-medium hover:bg-warning/90 transition-colors"
                  data-testid="replace-button"
                >
                  Replace
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UploadPage;
